import React, { useRef, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import InputBase from '@material-ui/core/InputBase';
import IconButton from '@material-ui/core/IconButton';
import './SearchBar.scss';

const SearchBar = ({
  context,
  onContextChange,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  debounce,
  id,
}) => {
  const paramValue = onChange ? value : (context && context.search || '');
  const [origValue, setOrigValue] = useState(paramValue);
  const [search, setSearchValue] = useState(paramValue);
  const timeout = useRef();
  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (paramValue !== origValue) {
      setOrigValue(paramValue);
      setSearchValue(paramValue);
    }
  }, [paramValue]);

  const notifyChange = useCallback((s) => {
    if (onChange) {
      onChange(s);
    } else if (onContextChange) {
      onContextChange(Object.assign({}, context, { search: s }));
    }
  }, [context, onChange, onContextChange]);

  const handleChange = useCallback((e) => {
    const { value: v } = e.target;
    setSearchValue(v);
    if (debounce) {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = setTimeout(() => {
        timeout.current = null;
        notifyChange(v);
      }, debounce);
    } else {
      notifyChange(v);
    }
  }, [notifyChange, debounce]);

  const handleDeleteText = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
    setSearchValue('');
    notifyChange('');
  }, []);

  return (
    <div className={classnames('tuc-search-bar', className)} id={id}>
      <div className="tuc-search-bar-icon tuc-search-bar-icon-no-btn">
        <svg className="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation">
          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          <path fill="none" d="M0 0h24v24H0z" />
        </svg>
      </div>
      <div className="tuc-search-bar-icon tuc-search-bar-icon-right">
        <IconButton aria-label="Delete" size="small" onClick={handleDeleteText} disabled={disabled}>
          <svg className="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            <path fill="none" d="M0 0h24v24H0z" />
          </svg>
        </IconButton>
      </div>
      <InputBase
        placeholder={placeholder}
        inputProps={{ 'aria-label': 'Search' }}
        value={search || ''}
        onChange={handleChange}
        disabled={disabled}
      />
    </div>
  );
};

SearchBar.propTypes = {
  context: PropTypes.shape({
    search: PropTypes.string,
  }),
  onContextChange: PropTypes.func,
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  debounce: PropTypes.number,
};

SearchBar.defaultProps = {
  context: undefined,
  onContextChange: undefined,
  value: undefined,
  onChange: undefined,
  className: undefined,
  placeholder: 'Search…',
  disabled: false,
  id: undefined,
  debounce: 200,
};

export default SearchBar;
